/**
 * Usage → Reports and upload — prototype VIEWS.usagerep (docs/prototype.html 3326-3344): one line per product
 * saying when its report last came in, then the latest numbers entered.
 *
 * "Product reports": GET /products (the rows, in Settings order) joined to GET /usage-reports → `products[]`
 * (state, label, clients in scope, latest period / who / file). "Reports entered": GET /usage/log (newest first,
 * already scoped: managers all, a member their own clients plus what they entered).
 */
import { useMemo, useState } from 'react';
import type { UploadsPage } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { currentPeriod, nf, periodLabel, shortDay, useProducts, useUploads, useUsageLog } from '../../lib/usage';
import { ErrorState, Loading } from '../../ui/kit';
import { Lbl } from '../../shell/Help';
import { UploadModal } from './UploadModal';

type Summary = UploadsPage['products'][number];
const STATE_TONE: Record<Summary['state'], string> = { in_this_month: 'hot', report_due: 'warm', not_uploaded: 'info' };
const LOG_N = 12;

export function UsageReports() {
  const [upload, setUpload] = useState<{ key?: string } | null>(null);
  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn pri" onClick={() => setUpload({})}>
            Upload usage report
          </button>
        </div>
      </div>
      <ProductReports onUpload={(key) => setUpload({ key })} />
      <ReportsEntered />
      {upload ? <UploadModal productKey={upload.key} onClose={() => setUpload(null)} /> : null}
    </>
  );
}

/** Prototype productReports(): one line per product — when its report last came in, who uploaded it, the file. */
export function ProductReports({ onUpload }: { onUpload: (productKey: string) => void }) {
  const pr = useProducts();
  const up = useUploads();
  const now = currentPeriod();
  const products = useMemo(() => [...(pr.data ?? [])].sort((a, b) => a.sort_order - b.sort_order), [pr.data]);
  const sum = useMemo(() => new Map((up.data?.products ?? []).map((s) => [s.product_key, s])), [up.data]);
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="hd">
        <h3>
          <Lbl>Product reports</Lbl>
        </h3>
        <div className="r">
          <span className="sub">One upload per product, for all its clients</span>
        </div>
      </div>
      {pr.isLoading || up.isLoading ? (
        <Loading label="Loading product reports…" />
      ) : pr.error || up.error ? (
        <ErrorState error={pr.error ?? up.error} onRetry={() => void (pr.error ? pr.refetch() : up.refetch())} />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Clients</th>
              <th>Latest report</th>
              <th>Uploaded by</th>
              <th>File</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const s = sum.get(p.key);
              const fresh = s?.state === 'in_this_month';
              return (
                <tr key={p.key}>
                  <td>
                    <b>{p.name}</b>
                    <div className="sub">{p.main_metric}</div>
                  </td>
                  <td className="num">{s?.clients ?? 0}</td>
                  <td>
                    {s?.last_period ? periodLabel(s.last_period, now) : '-'}
                    <div style={{ marginTop: 3 }}>
                      <span className={`chip ${STATE_TONE[s?.state ?? 'not_uploaded']}`}>{s?.label ?? 'Not uploaded yet'}</span>
                    </div>
                  </td>
                  <td>
                    {s?.last_uploaded_by ? (
                      <>
                        {firstName(s.last_uploaded_by.name)}
                        {s.last_uploaded_at ? <div className="sub">{shortDay(s.last_uploaded_at)}</div> : null}
                      </>
                    ) : s?.last_uploaded_at ? (
                      <span className="sub">Automatic</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{s?.last_filename ? <span className="chip info">{s.last_filename}</span> : <span className="sub">-</span>}</td>
                  <td className="tr">
                    <button className={`btn sm${fresh ? '' : ' pri'}`} onClick={() => onUpload(p.key)}>
                      Upload report
                    </button>
                  </td>
                </tr>
              );
            })}
            {!products.length ? (
              <tr>
                <td colSpan={6} className="sub">
                  No products are set up yet. Add one in Settings → Products and usage.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Prototype usageLog(): the latest numbers entered (GET /usage/log, already scoped). */
export function ReportsEntered() {
  const pr = useProducts();
  const log = useUsageLog(LOG_N);
  const now = currentPeriod();
  const byKey = useMemo(() => new Map((pr.data ?? []).map((p) => [p.key, p])), [pr.data]);
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="hd">
        <h3>
          <Lbl>Reports entered</Lbl>
        </h3>
        <div className="r">
          <span className="sub">{log.data ? `${log.data.data.length} latest` : ''}</span>
        </div>
      </div>
      {log.isLoading ? (
        <Loading label="Loading reports entered…" />
      ) : log.error ? (
        <ErrorState error={log.error} onRetry={() => void log.refetch()} />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Product</th>
              <th>Period</th>
              <th>Numbers</th>
              <th>Entered by</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {log.data!.data.map((u) => {
              const p = byKey.get(u.product_key);
              const extras = (p?.fields ?? []).filter((f) => !f.is_main && u.extras[f.field_key] != null);
              return (
                <tr key={u.id}>
                  <td>
                    <b>{u.client.name}</b>
                  </td>
                  <td>{p?.name ?? u.product_key}</td>
                  <td>{periodLabel(u.period, now)}</td>
                  <td style={{ fontSize: 12.5 }}>
                    {p?.main_metric ?? 'Main number'} <b>{nf(u.main_value)}</b>
                    {extras.map((f) => (
                      <span key={f.field_key}>
                        {' · '}
                        {f.label}{' '}
                        <b>
                          {nf(u.extras[f.field_key])}
                          {f.unit === '%' ? '%' : ''}
                        </b>
                      </span>
                    ))}
                  </td>
                  <td>
                    {u.uploaded_by ? firstName(u.uploaded_by.name) : 'Automatic'}
                    <div className="sub">{shortDay(u.at)}</div>
                  </td>
                  <td>
                    {u.original_filename ? (
                      <span className="chip info">{u.original_filename}</span>
                    ) : (
                      <span className="sub">{u.source === 'api' ? 'pulled automatically' : 'typed in'}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!log.data!.data.length ? (
              <tr>
                <td colSpan={6} className="sub">
                  No reports entered yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  );
}
